/**
 * Shared corrective-action panel helpers — one model for every section.
 * Out-of-range readings always save; an action recorded with (or after) the
 * reading resolves its exception, and a recheck is optional extra evidence.
 */

import {
  FOOD_SAFETY_CALIBRATION_DEFAULTS,
  FOOD_SAFETY_CORRECTIVE_ACTION,
  FOOD_SAFETY_CORRECTIVE_ACTION_LABELS,
  FOOD_SAFETY_CORRECTIVE_ACTIONS_REQUIRING_NOTE,
  FOOD_SAFETY_EXCEPTION_KIND,
  FOOD_SAFETY_EXCEPTION_STATUS,
  FOOD_SAFETY_RECHECK_REMINDER_MINUTES,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'
import { formatFoodSafetyClockTime } from 'src/utils/foodSafetyTime'
import { formatFoodSafetyTempRange } from 'src/utils/foodSafetyTempRange'
import {
  FOOD_SAFETY_IMPOSSIBLE_READING_NOTE,
  foodSafetyTemperatureBoundError,
} from 'src/utils/foodSafetyTemperatureBounds'

export const FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH = 1000

const {
  MOVED_FOOD,
  ADJUSTED_EQUIPMENT,
  CALLED_ENGINEER,
  DISCARDED_FOOD,
  REJECTED_DELIVERY,
  REHEATED,
  COOKED_LONGER,
  CLEANED_AGAIN,
  REPLACED_PROBE,
  PROBE_OUT_OF_USE,
  OTHER,
} = FOOD_SAFETY_CORRECTIVE_ACTION

const DEFAULT_ACTIONS = Object.freeze([
  MOVED_FOOD,
  ADJUSTED_EQUIPMENT,
  CALLED_ENGINEER,
  DISCARDED_FOOD,
  OTHER,
])

/**
 * Chips offered per section, in order — mirrors Express. Express refuses the
 * section-only actions (Reheated, Cooked longer, …) on any other section.
 */
export const FOOD_SAFETY_CORRECTIVE_ACTIONS_BY_SECTION = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: DEFAULT_ACTIONS,
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: Object.freeze([
    COOKED_LONGER,
    REHEATED,
    DISCARDED_FOOD,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.COOLING]: Object.freeze([
    MOVED_FOOD,
    DISCARDED_FOOD,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: Object.freeze([
    REHEATED,
    ADJUSTED_EQUIPMENT,
    DISCARDED_FOOD,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: Object.freeze([
    MOVED_FOOD,
    ADJUSTED_EQUIPMENT,
    DISCARDED_FOOD,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.DELIVERIES]: Object.freeze([
    REJECTED_DELIVERY,
    MOVED_FOOD,
    DISCARDED_FOOD,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.DISHWASHERS]: Object.freeze([
    ADJUSTED_EQUIPMENT,
    CALLED_ENGINEER,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: Object.freeze([
    ADJUSTED_EQUIPMENT,
    REPLACED_PROBE,
    PROBE_OUT_OF_USE,
    OTHER,
  ]),
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: Object.freeze([
    CLEANED_AGAIN,
    CALLED_ENGINEER,
    OTHER,
  ]),
})

export function foodSafetyCorrectiveActionOptions(section) {
  const types =
    FOOD_SAFETY_CORRECTIVE_ACTIONS_BY_SECTION[section] || DEFAULT_ACTIONS
  return types.map(type => ({
    type,
    label: FOOD_SAFETY_CORRECTIVE_ACTION_LABELS[type],
  }))
}

const NOTE_EXAMPLES = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: 'moved stock to walk-in 2',
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: 'cooked 5 more minutes, now 78°C',
  [FOOD_SAFETY_SECTION.COOLING]: 'split into shallow trays',
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: 'reheated to 75°C',
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: 'moved to the walk-in',
  [FOOD_SAFETY_SECTION.DELIVERIES]: 'put straight in the freezer',
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: 'new batteries, tested again',
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: 'cleaned again, told the manager',
})

/** Note placeholder with an example that fits the section. */
export function foodSafetyCorrectiveNotePlaceholder(
  section,
  { required } = {},
) {
  const example = NOTE_EXAMPLES[section]
  if (required) {
    return example ? `Say what was done — e.g. ${example}` : 'Say what was done'
  }
  return example ? `Note (optional) — e.g. ${example}` : 'Note (optional)'
}

/**
 * Recheck wording. A probe is retested (Pass / Fail against its expected
 * reading); everything else is a temperature back in range or not.
 */
export function foodSafetyRecheckWording(section) {
  if (section === FOOD_SAFETY_SECTION.PROBE_CALIBRATION) {
    return {
      fieldLabel: 'Retest — probe reads',
      openLabel: '+ Retest now',
      remindLabel: 'Retest by (optional)',
      followUpLabel: 'Record retest',
      passLabel: 'Pass',
      failLabel: 'Fail',
    }
  }
  return {
    fieldLabel: 'Recheck temp',
    openLabel: '+ Record recheck now',
    remindLabel: 'Recheck by (optional)',
    followUpLabel: 'Record recheck',
    passLabel: 'Back in range',
    failLabel: 'Still outside range',
  }
}

export const FOOD_SAFETY_CORRECTIVE_FIELD = Object.freeze({
  ACTIONS: 'actions',
  NOTE: 'note',
  RECHECK: 'recheck',
})

/**
 * Which part of the panel needs the user next: the note when "Other" has
 * none (or it's too long), the recheck when it isn't a number, else the
 * action chips (or the recheck when actions aren't offered).
 */
export function resolveFoodSafetyCorrectiveFocusField(
  draft = {},
  { showActions = true, rejected = false } = {},
) {
  const notes = String(draft.notes || '').trim()
  if (rejected) return FOOD_SAFETY_CORRECTIVE_FIELD.NOTE
  if (
    (draft.type && foodSafetyCorrectiveNoteRequired(draft.type) && !notes) ||
    notes.length > FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH
  ) {
    return FOOD_SAFETY_CORRECTIVE_FIELD.NOTE
  }
  const recheckText = String(draft.recheckText || '').trim()
  if (
    draft.recheckOpen &&
    recheckText &&
    parseFoodSafetyRecheckValue(recheckText) == null
  ) {
    return FOOD_SAFETY_CORRECTIVE_FIELD.RECHECK
  }
  if (showActions) return FOOD_SAFETY_CORRECTIVE_FIELD.ACTIONS
  return draft.recheckOpen
    ? FOOD_SAFETY_CORRECTIVE_FIELD.RECHECK
    : FOOD_SAFETY_CORRECTIVE_FIELD.ACTIONS
}

/**
 * Move focus into a corrective panel by its `testIdPrefix`: the chosen chip
 * (else the first), the note, or the recheck field. Falls back to whatever
 * the panel has.
 */
export function focusFoodSafetyCorrectiveField(testIdPrefix, field) {
  if (typeof document === 'undefined' || !testIdPrefix) return
  const find = selector => document.querySelector(selector)
  const run = () => {
    const root = find(`[data-testid="${testIdPrefix}"]`)
    if (!root) return
    const note = find(`[data-testid="${testIdPrefix}-note"]`)
    const recheck = find(`[data-testid="${testIdPrefix}-recheck-input"]`)
    const chip =
      root.querySelector(
        `[data-testid^="${testIdPrefix}-chip-"][aria-pressed="true"]`,
      ) || root.querySelector(`[data-testid^="${testIdPrefix}-chip-"]`)
    const byField = {
      [FOOD_SAFETY_CORRECTIVE_FIELD.NOTE]: note,
      [FOOD_SAFETY_CORRECTIVE_FIELD.RECHECK]: recheck,
      [FOOD_SAFETY_CORRECTIVE_FIELD.ACTIONS]: chip,
    }
    const target = byField[field] || chip || note || recheck
    target?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    target?.focus?.()
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
  else run()
}

const toNumberOrNull = value =>
  value == null || value === '' || Number.isNaN(Number(value))
    ? null
    : Number(value)

/** "9°C" — keeps one decimal when the reading has one. */
export function formatFoodSafetyTemp(value) {
  const n = toNumberOrNull(value)
  return n == null ? '' : `${n}°C`
}

/**
 * Panel headline, e.g. "⚠ 9°C — outside 0–5°C".
 * @param {{ value?: number|string|null, tempMin?: number|null, tempMax?: number|null }} reading
 */
export function formatFoodSafetyOutOfRangeHeadline({
  value,
  tempMin,
  tempMax,
} = {}) {
  const temp = formatFoodSafetyTemp(value)
  const min = toNumberOrNull(tempMin)
  const max = toNumberOrNull(tempMax)
  let range = 'outside range'
  if (min != null && max != null)
    range = `outside ${formatFoodSafetyTempRange(min, max)}`
  else if (min != null) range = `below ${min}°C minimum`
  else if (max != null) range = `above ${max}°C maximum`
  return temp ? `⚠ ${temp} — ${range}` : `⚠ ${range}`
}

/** What screen readers hear once when a corrective-action panel opens. */
export function foodSafetyCorrectiveAnnouncement({ variant = 'action' } = {}) {
  return variant === 'rejected'
    ? 'Delivery rejected. Say why below.'
    : 'Needs corrective action. Record it below.'
}

export const EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT = Object.freeze({
  type: null,
  notes: '',
  recheckOpen: false,
  recheckText: '',
  recheckInMinutes: null,
})

export function foodSafetyCorrectiveNoteRequired(type) {
  return FOOD_SAFETY_CORRECTIVE_ACTIONS_REQUIRING_NOTE.includes(type)
}

/**
 * Message when the draft can't be sent as-is, else null. No action chosen is
 * fine — the reading still saves and the exception stays open.
 */
export function validateFoodSafetyCorrectiveDraft(draft = {}) {
  const notes = String(draft.notes || '').trim()
  if (draft.type && foodSafetyCorrectiveNoteRequired(draft.type) && !notes) {
    return 'Add a note to say what was done.'
  }
  if (notes.length > FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH) {
    return `Keep the note under ${FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH} characters.`
  }
  if (draft.recheckOpen && String(draft.recheckText || '').trim()) {
    if (parseFoodSafetyRecheckValue(draft.recheckText) == null) {
      return 'Enter the recheck temperature as a number.'
    }
  }
  return null
}

/**
 * The "Recheck by" minutes to send, or null. Only with an action, and not
 * when the recheck itself is being entered now.
 */
export function resolveFoodSafetyRecheckReminder(draft = {}) {
  const minutes = Number(draft.recheckInMinutes)
  if (!draft.type || !FOOD_SAFETY_RECHECK_REMINDER_MINUTES.includes(minutes)) {
    return null
  }
  if (draft.recheckOpen && String(draft.recheckText || '').trim()) return null
  return minutes
}

/**
 * `{ type, notes }` (+ `recheckInMinutes`) for Express, or null when no
 * action was chosen.
 */
export function buildFoodSafetyCorrectiveActionBody(draft = {}) {
  if (!draft.type) return null
  const body = { type: draft.type, notes: String(draft.notes || '').trim() }
  const reminder = resolveFoodSafetyRecheckReminder(draft)
  if (reminder != null) body.recheckInMinutes = reminder
  return body
}

/** Pending "recheck by" reminder: due, no recheck recorded yet. */
export function hasFoodSafetyRecheckDue(exception) {
  return Boolean(exception?.recheckDueAt) && !exception?.recheck?.logId
}

/** True once a pending reminder's time has passed. */
export function isFoodSafetyRecheckOverdue(exception, nowMs = Date.now()) {
  if (!hasFoodSafetyRecheckDue(exception)) return false
  const due = new Date(exception.recheckDueAt).getTime()
  return Number.isFinite(due) && due <= nowMs
}

/** "Recheck due 14:30", or "Recheck overdue — was due 14:30". */
export function formatFoodSafetyRecheckDue(exception, nowMs = Date.now()) {
  if (!hasFoodSafetyRecheckDue(exception)) return ''
  const time = formatFoodSafetyClockTime(exception.recheckDueAt)
  if (!time) return ''
  return isFoodSafetyRecheckOverdue(exception, nowMs)
    ? `Recheck overdue — was due ${time}`
    : `Recheck due ${time}`
}

export function parseFoodSafetyRecheckValue(text) {
  const raw = String(text ?? '')
    .trim()
    .replace(/°c?$/i, '')
    .replace(',', '.')
  if (!raw || raw === '-' || raw === '.') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Same range rule Express uses for the recheck (null range ⇒ never outside). */
export function isFoodSafetyTempOutsideRange(value, { tempMin, tempMax } = {}) {
  const n = toNumberOrNull(value)
  if (n == null) return false
  const min = toNumberOrNull(tempMin)
  const max = toNumberOrNull(tempMax)
  return (min != null && n < min) || (max != null && n > max)
}

/** Recheck temperature to send, or null when none was entered. */
export function resolveFoodSafetyRecheckValue(draft = {}) {
  if (!draft.recheckOpen) return null
  return parseFoodSafetyRecheckValue(draft.recheckText)
}

const RECHECKABLE_KINDS = Object.freeze([
  FOOD_SAFETY_EXCEPTION_KIND.OUT_OF_RANGE,
  FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED,
])

export function isFoodSafetyExceptionActionable(exception) {
  return Boolean(exception?._id) && !exception.legacy
}

/**
 * The exception a logged reading still carries (open first). Legacy V1 notes
 * are read-only evidence, never actionable.
 */
export function pickFoodSafetyLogException(log) {
  const list = Array.isArray(log?.exceptions) ? log.exceptions : []
  return (
    list.find(e => e?.status === FOOD_SAFETY_EXCEPTION_STATUS.OPEN) ||
    list[0] ||
    null
  )
}

export function isFoodSafetyExceptionOpen(exception) {
  return (
    isFoodSafetyExceptionActionable(exception) &&
    exception.status === FOOD_SAFETY_EXCEPTION_STATUS.OPEN
  )
}

/**
 * Rechecks are logged on today's date, so only offer them on today's
 * screen — never against a past day.
 */
export function canRecordFoodSafetyRecheck(exception, { viewingToday } = {}) {
  return (
    Boolean(viewingToday) &&
    isFoodSafetyExceptionActionable(exception) &&
    !exception.resolvedByCorrection &&
    RECHECKABLE_KINDS.includes(exception.kind) &&
    exception.section !== FOOD_SAFETY_SECTION.DELIVERIES &&
    !exception.recheck?.logId
  )
}

/** "Moved food — into walk-in 2" for the latest recorded action. */
export function formatFoodSafetyLatestCorrectiveAction(exception) {
  const actions = Array.isArray(exception?.correctiveActions)
    ? exception.correctiveActions
    : []
  const latest = actions[actions.length - 1]
  if (!latest) return ''
  const label = FOOD_SAFETY_CORRECTIVE_ACTION_LABELS[latest.type] || ''
  const notes = String(latest.notes || '').trim()
  if (latest.type === OTHER) return notes || label
  return notes ? `${label} — ${notes}` : label
}

const localDayKey = date =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

/** "14:32", or "Thu 1 Oct 14:32" when it isn't the same day as `sameDayAs`. */
function formatFoodSafetyRecordedWhen(at, sameDayAs) {
  const time = formatFoodSafetyClockTime(at)
  if (!time) return ''
  const date = new Date(at)
  const ref = sameDayAs ? new Date(sameDayAs) : null
  if (!ref || Number.isNaN(ref.getTime())) return time
  if (localDayKey(date) === localDayKey(ref)) return time
  const day = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
  return `${day} ${time}`
}

const ACTION_WITH_READING_MS = 2 * 60 * 1000

/**
 * Who recorded the latest action and when: "Sam Smith · 14:32" (adds the
 * day when it was recorded on a later day than the reading). '' when it was
 * saved with the reading by the same person — the reading's line says so.
 */
export function formatFoodSafetyCorrectiveActionBy(exception) {
  const actions = Array.isArray(exception?.correctiveActions)
    ? exception.correctiveActions
    : []
  const latest = actions[actions.length - 1]
  if (!latest) return ''
  const recordedMs = new Date(latest.recordedAt).getTime()
  const raisedMs = new Date(exception?.raisedAt).getTime()
  const sameMoment =
    Number.isFinite(recordedMs) &&
    Number.isFinite(raisedMs) &&
    Math.abs(recordedMs - raisedMs) <= ACTION_WITH_READING_MS
  const samePerson =
    !latest.recordedBy ||
    !exception?.raisedBy ||
    String(latest.recordedBy) === String(exception.raisedBy)
  if (sameMoment && samePerson) return ''
  const name = String(latest.recordedByName || '').trim()
  const when = formatFoodSafetyRecordedWhen(
    latest.recordedAt,
    exception?.raisedAt,
  )
  return [name, when].filter(Boolean).join(' · ')
}

/**
 * "Recheck 4°C — back in range · 14:32" / "Recheck 9°C — still outside
 * range · 14:32". Probe retests keep "Recheck 0.3°C · 14:32".
 */
export function formatFoodSafetyRecheck(recheck, { kind, raisedAt } = {}) {
  if (!recheck || recheck.value == null) return ''
  const base = `Recheck ${formatFoodSafetyTemp(recheck.value)}`
  let line
  if (kind === FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED) {
    const result =
      String(recheck.resultLabel || '').trim() ||
      (recheck.outOfRange ? 'Fail' : 'Pass')
    line = `${base} — ${result}`
  } else {
    line = recheck.outOfRange
      ? `${base} — still outside range`
      : `${base} — back in range`
  }
  const when = formatFoodSafetyRecordedWhen(recheck.recordedAt, raisedAt)
  return when ? `${line} · ${when}` : line
}

export function isFoodSafetyRecheckLog(log) {
  return Boolean(log?.recheckOfException)
}

/** "⚠ Delivery rejected" (+ the temp when it was also outside range). */
export function formatFoodSafetyRejectedHeadline({
  value,
  tempMin,
  tempMax,
} = {}) {
  if (isFoodSafetyTempOutsideRange(value, { tempMin, tempMax })) {
    return `⚠ Delivery rejected — ${formatFoodSafetyOutOfRangeHeadline({
      value,
      tempMin,
      tempMax,
    })
      .replace(/^⚠ /, '')
      .replace(' — ', ', ')}`
  }
  return '⚠ Delivery rejected'
}

/** "Ice water" / "Boiling water" for a probe exception, or ''. */
export function foodSafetyCalibrationTestLabel(exception) {
  return (
    FOOD_SAFETY_CALIBRATION_DEFAULTS[exception?.context?.calibrationTest]
      ?.label || ''
  )
}

/**
 * "⚠ Boiling water: probe read 97°C — expected 100°C ±1°C". Old records with
 * no target keep "⚠ Probe calibration failed — read 97°C".
 */
function formatFoodSafetyCalibrationHeadline(exception, reading) {
  const withOldReadingNote = headline =>
    foodSafetyTemperatureBoundError(reading.value)
      ? `${headline}. ${FOOD_SAFETY_IMPOSSIBLE_READING_NOTE}`
      : headline
  const temp = formatFoodSafetyTemp(reading.value)
  const target = toNumberOrNull(reading.target)
  const test = exception?.context?.calibrationTest
  if (test === 'wash' || test === 'rinse') {
    const label = test === 'wash' ? 'Wash' : 'Final rinse'
    const expected =
      target != null ? ` — at or above ${formatFoodSafetyTemp(target)}` : ''
    return withOldReadingNote(`⚠ ${label}: read ${temp || '—'}${expected}`)
  }
  if (target == null || !temp) {
    return withOldReadingNote(
      `⚠ Probe calibration failed${temp ? ` — read ${temp}` : ''}`,
    )
  }
  const max = toNumberOrNull(reading.tempMax)
  const min = toNumberOrNull(reading.tempMin)
  const tolerance =
    max != null
      ? Math.round((max - target) * 100) / 100
      : min != null
      ? Math.round((target - min) * 100) / 100
      : null
  const expected = `expected ${formatFoodSafetyTemp(target)}${
    tolerance != null ? ` ±${formatFoodSafetyTemp(tolerance)}` : ''
  }`
  const label = foodSafetyCalibrationTestLabel(exception)
  const headline = label
    ? `⚠ ${label}: probe read ${temp} — ${expected}`
    : `⚠ Probe read ${temp} — ${expected}`
  return withOldReadingNote(headline)
}

/** Headline for a saved exception — says what actually went wrong. */
export function formatFoodSafetyExceptionHeadline(exception, fallbackReading) {
  const reading = exception?.reading || fallbackReading || {}
  switch (exception?.kind) {
    case FOOD_SAFETY_EXCEPTION_KIND.DELIVERY_REJECTED:
      return formatFoodSafetyRejectedHeadline(reading)
    case FOOD_SAFETY_EXCEPTION_KIND.COOLING_TIME_EXCEEDED: {
      const took = Number(exception?.context?.durationMinutes)
      const max = Number(exception?.context?.maxMinutes)
      if (took > 0 && max > 0) {
        return `⚠ Cooling took ${took} min — over the ${max} min limit`
      }
      return '⚠ Cooling took too long'
    }
    case FOOD_SAFETY_EXCEPTION_KIND.HOLDING_TIME_EXCEEDED: {
      const held = Number(exception?.context?.durationMinutes)
      const max = Number(exception?.context?.maxMinutes)
      if (held > 0 && max > 0) {
        return `⚠ Held ${held} min — over the ${max} min limit`
      }
      return '⚠ Held too long'
    }
    case FOOD_SAFETY_EXCEPTION_KIND.CHECKLIST_ISSUE: {
      const step = String(exception?.context?.stepLabel || '').trim()
      return step ? `⚠ Issue — ${step}` : '⚠ Checklist issue'
    }
    case FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED:
      return formatFoodSafetyCalibrationHeadline(exception, reading)
    default:
      return formatFoodSafetyOutOfRangeHeadline(reading)
  }
}

/** Follow-up draft for a saved exception (rejected ⇒ "Rejected delivery" chosen). */
export function initialFoodSafetyFollowUpDraft(exception, patch = {}) {
  return {
    ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
    type:
      exception?.kind === FOOD_SAFETY_EXCEPTION_KIND.DELIVERY_REJECTED
        ? REJECTED_DELIVERY
        : null,
    ...patch,
  }
}

/** "Today · 09:15", "Yesterday · 18:02", or "Thu 1 Oct". */
export function formatFoodSafetyExceptionWhen(
  exception,
  { todayLocalDate } = {},
) {
  const localDate = String(exception?.localDate || '')
  const time = formatFoodSafetyClockTime(exception?.raisedAt)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate)
  if (!match) return time
  const day = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], 12))
  if (localDate === todayLocalDate) return time ? `Today · ${time}` : 'Today'
  const todayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
    String(todayLocalDate || ''),
  )
  if (todayMatch) {
    const today = Date.UTC(
      +todayMatch[1],
      +todayMatch[2] - 1,
      +todayMatch[3],
      12,
    )
    if (today - day.getTime() === 24 * 60 * 60 * 1000) {
      return time ? `Yesterday · ${time}` : 'Yesterday'
    }
  }
  return day.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/** Open, actionable exceptions across a card's logs (for its badge). */
export function countFoodSafetyOpenExceptions(logs) {
  return (Array.isArray(logs) ? logs : []).reduce(
    (sum, log) =>
      sum +
      (Array.isArray(log?.exceptions) ? log.exceptions : []).filter(
        isFoodSafetyExceptionOpen,
      ).length,
    0,
  )
}

/** Amber card badge: "Needs action" / "Needs action · 2", or ''. */
export function formatFoodSafetyNeedsActionBadge(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return n === 1 ? 'Needs action' : `Needs action · ${n}`
}

/** Home chip → section link that jumps to the first open exception. */
export const FOOD_SAFETY_NEEDS_ACTION_PARAM = 'needsAction'

/**
 * Scroll to and focus an exception's in-page "Record corrective action"
 * link. Returns false when the page doesn't show it.
 */
export function jumpToFoodSafetyException(exceptionId) {
  if (typeof document === 'undefined' || !exceptionId) return false
  const target = document.querySelector(
    `[data-fs-exception-action="${exceptionId}"]`,
  )
  if (!target) return false
  target.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  target.focus?.()
  return true
}

/** "1 item needs corrective action" / "3 items need corrective action". */
export function formatFoodSafetyUnresolvedTitle(count) {
  const n = Number(count) || 0
  return n === 1
    ? '1 item needs corrective action'
    : `${n} items need corrective action`
}
